/**
 * MyNotesPage — full-page notes browser grouped domain > tutorial > scene.
 *
 * Fetches GET /scenario-os/curriculum/notes and renders a 3-level
 * collapsible hierarchy. Each note card has a Delete button.
 * A search bar at the top filters notes by text content.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LearnerNav from '../../components/nav/LearnerNav';

function getToken() {
  return localStorage.getItem('scenario_os_token');
}

// ── Grouping helpers ───────────────────────────────────────────────────────

function groupNotes(notes) {
  const domains = {};
  for (const note of notes) {
    const domain = note.domain || 'General';
    // Group by human titles when the API enriched them; fall back to ids.
    const tutorialKey = note.tutorial_id || '(No Tutorial)';
    const tutorialLabel = note.tutorial_title || tutorialKey;
    const sceneKey = note.scene_id || '(No Scene)';
    const sceneLabel = note.scene_title || sceneKey;
    if (!domains[domain]) domains[domain] = {};
    if (!domains[domain][tutorialKey]) {
      domains[domain][tutorialKey] = { label: tutorialLabel, scenes: {} };
    }
    if (!domains[domain][tutorialKey].scenes[sceneKey]) {
      domains[domain][tutorialKey].scenes[sceneKey] = { label: sceneLabel, notes: [] };
    }
    domains[domain][tutorialKey].scenes[sceneKey].notes.push(note);
  }
  return domains;
}

function formatAnchor(note) {
  if (note.timestamp_sec != null) {
    const m = Math.floor(note.timestamp_sec / 60);
    const s = String(Math.floor(note.timestamp_sec % 60)).padStart(2, '0');
    return `@ ${m}:${s}`;
  }
  if (note.slide_index != null) return `Slide ${note.slide_index + 1}`;
  return null;
}

function noteTypeBadge(type) {
  if (type === 'bookmark') return { label: 'Bookmark', bg: '#f59e0b', color: '#1e293b' };
  if (type === 'slide') return { label: 'Slide', bg: '#6366f1', color: '#fff' };
  return { label: 'Note', bg: '#3b82f6', color: '#fff' };
}

// ── Sub-components ─────────────────────────────────────────────────────────

function NoteCard({ note, onDelete, deleting }) {
  const anchor = formatAnchor(note);
  const badge = noteTypeBadge(note.note_type);
  return (
    <div style={noteCardStyle}>
      <div style={noteCardHeaderStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ ...badgeStyle, background: badge.bg, color: badge.color }}>
            {badge.label}
          </span>
          {anchor && (
            <span style={{ fontSize: 11, color: '#6a6a72' }}>{anchor}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: '#6a6a72' }}>
            {new Date(note.created_at).toLocaleDateString()}
          </span>
          <button
            onClick={() => onDelete(note.note_id)}
            disabled={deleting === note.note_id}
            title="Delete note"
            style={{
              ...deleteBtnStyle,
              opacity: deleting === note.note_id ? 0.4 : 1,
              cursor: deleting === note.note_id ? 'default' : 'pointer',
            }}
          >
            Delete
          </button>
        </div>
      </div>
      <p style={noteTextStyle}>{note.text}</p>
    </div>
  );
}

function SceneSection({ sceneLabel, notes, onDelete, deleting }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={sceneSectionStyle}>
      <button onClick={() => setOpen((v) => !v)} style={sceneTitleStyle}>
        <span style={{ marginRight: 6, fontSize: 10, color: '#6a6a72' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: 12, color: '#6a6a72' }}>Scene: </span>
        <span style={{ fontSize: 12, color: '#1E1E24', fontWeight: 600 }}>{sceneLabel}</span>
        <span style={{ marginLeft: 8, fontSize: 10, color: '#6a6a72' }}>
          ({notes.length} note{notes.length !== 1 ? 's' : ''})
        </span>
      </button>
      {open && (
        <div style={{ paddingLeft: 12 }}>
          {notes.map((note) => (
            <NoteCard
              key={note.note_id}
              note={note}
              onDelete={onDelete}
              deleting={deleting}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TutorialSection({ tutorialId, tutorialLabel, scenes, onDelete, deleting }) {
  const [open, setOpen] = useState(true);
  const totalNotes = Object.values(scenes).reduce((acc, bucket) => acc + bucket.notes.length, 0);
  return (
    <div style={tutorialSectionStyle}>
      <button onClick={() => setOpen((v) => !v)} style={tutorialTitleStyle}>
        <span style={{ marginRight: 6, fontSize: 11, color: '#92140C' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: 13, color: '#1E1E24', fontWeight: 600 }}>
          {tutorialId === '(No Tutorial)' ? 'Unassigned' : tutorialLabel}
        </span>
        <span style={{ marginLeft: 8, fontSize: 11, color: '#6a6a72' }}>
          ({totalNotes} note{totalNotes !== 1 ? 's' : ''})
        </span>
      </button>
      {open && (
        <div style={{ paddingLeft: 8 }}>
          {Object.entries(scenes).map(([sceneId, bucket]) => (
            <SceneSection
              key={sceneId}
              sceneLabel={bucket.label || sceneId}
              notes={bucket.notes}
              onDelete={onDelete}
              deleting={deleting}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DomainSection({ domain, tutorials, onDelete, deleting }) {
  const [open, setOpen] = useState(true);
  const totalNotes = Object.values(tutorials).reduce(
    (acc, tut) => acc + Object.values(tut.scenes).reduce((a, bucket) => a + bucket.notes.length, 0),
    0,
  );
  return (
    <div style={domainSectionStyle}>
      <button onClick={() => setOpen((v) => !v)} style={domainTitleStyle}>
        <span style={{ marginRight: 8, fontSize: 12, color: '#FFCF99' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#FFF8F0', textTransform: 'capitalize' }}>
          {domain.replace(/_/g, ' ')}
        </span>
        <span style={{ marginLeft: 10, fontSize: 12, color: 'rgba(255,207,153,0.7)', fontWeight: 400 }}>
          {totalNotes} note{totalNotes !== 1 ? 's' : ''}
        </span>
      </button>
      {open && (
        <div style={{ paddingTop: 4 }}>
          {Object.entries(tutorials).map(([tutorialId, tut]) => (
            <TutorialSection
              key={tutorialId}
              tutorialId={tutorialId}
              tutorialLabel={tut.label || tutorialId}
              scenes={tut.scenes}
              onDelete={onDelete}
              deleting={deleting}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function MyNotesPage() {
  const navigate = useNavigate();
  const [allNotes, setAllNotes] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch('/scenario-os/curriculum/notes', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAllNotes(data.notes || []);
    } catch {
      setAllNotes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleDelete = async (noteId) => {
    setDeleting(noteId);
    try {
      const token = getToken();
      await fetch(`/scenario-os/curriculum/notes/${noteId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setAllNotes((prev) => prev.filter((n) => n.note_id !== noteId));
    } finally {
      setDeleting(null);
    }
  };

  const filteredNotes = search.trim()
    ? allNotes.filter((n) =>
        (n.text || '').toLowerCase().includes(search.toLowerCase()),
      )
    : allNotes;

  const grouped = groupNotes(filteredNotes);
  const isEmpty = filteredNotes.length === 0;

  return (
    <div style={pageStyle}>
      <LearnerNav />
      <div style={pageInnerStyle}>
        <h2 style={pageTitleStyle}>My Notes</h2>

        <input
          type="text"
          placeholder="Search notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={searchInputStyle}
        />

        {loading ? (
          <div style={emptyStateStyle}>Loading…</div>
        ) : isEmpty ? (
          <div style={emptyStateStyle}>
            {search
              ? 'No notes match your search.'
              : 'No notes yet. Start taking notes as you learn!'}
          </div>
        ) : (
          Object.entries(grouped).map(([domain, tutorials]) => (
            <DomainSection
              key={domain}
              domain={domain}
              tutorials={tutorials}
              onDelete={handleDelete}
              deleting={deleting}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── Styles (TeachIQ cream palette) ─────────────────────────────────────────

const pageStyle = {
  minHeight: '100vh',
  background: '#FFF8F0',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const pageInnerStyle = {
  maxWidth: 820,
  margin: '0 auto',
  padding: '32px 16px',
};

const pageTitleStyle = {
  fontSize: 24,
  fontWeight: 700,
  color: '#1E1E24',
  margin: '0 0 20px',
};

const searchInputStyle = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: 8,
  border: '1px solid #FFCF99',
  background: '#fff',
  color: '#1E1E24',
  fontSize: 14,
  outline: 'none',
  marginBottom: 24,
  boxSizing: 'border-box',
};

const emptyStateStyle = {
  textAlign: 'center',
  padding: '60px 0',
  color: '#6a6a72',
  fontSize: 15,
};

const domainSectionStyle = {
  background: '#fff',
  borderRadius: 10,
  border: '1px solid #FFCF99',
  marginBottom: 16,
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(30,30,36,0.06)',
};

const domainTitleStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  background: '#1E1E24',
  border: 'none',
  borderBottom: '1px solid #FFCF99',
  padding: '14px 16px',
  cursor: 'pointer',
  textAlign: 'left',
};

const tutorialSectionStyle = {
  borderBottom: '1px solid #FFF8F0',
  margin: '0 12px 0',
};

const tutorialTitleStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  background: 'none',
  border: 'none',
  borderBottom: '1px solid rgba(255,207,153,0.3)',
  padding: '10px 4px',
  cursor: 'pointer',
  textAlign: 'left',
};

const sceneSectionStyle = {
  paddingTop: 4,
  paddingBottom: 4,
};

const sceneTitleStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  background: 'none',
  border: 'none',
  padding: '6px 0',
  cursor: 'pointer',
  textAlign: 'left',
};

const noteCardStyle = {
  background: '#FFF8F0',
  border: '1px solid rgba(255,207,153,0.5)',
  borderRadius: 8,
  padding: '10px 14px',
  marginBottom: 8,
};

const noteCardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 6,
};

const badgeStyle = {
  fontSize: 10,
  fontWeight: 700,
  padding: '2px 7px',
  borderRadius: 999,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const noteTextStyle = {
  margin: 0,
  fontSize: 13,
  color: '#1E1E24',
  lineHeight: 1.6,
};

const deleteBtnStyle = {
  background: 'none',
  border: '1px solid #FFCF99',
  borderRadius: 4,
  color: '#92140C',
  fontSize: 11,
  padding: '2px 8px',
  cursor: 'pointer',
};
